import { useRef, type ReactNode, type MouseEvent } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface MagneticButtonProps {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  variant?: "primary" | "outline";
  className?: string;
  fullWidth?: boolean;
}

/**
 * Button with magnetic hover (follows the cursor softly) and a gold shine sweep.
 * Uses only existing brand tokens (accent / primary).
 */
const MagneticButton = ({ children, onClick, href, type, disabled, variant = "primary", className, fullWidth }: MagneticButtonProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 180, damping: 16, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 180, damping: 16, mass: 0.4 });

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    if (reducedMotion || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    x.set(relX * 0.25);
    y.set(relY * 0.35);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: springX, y: springY }}
      className={cn("inline-block", fullWidth && "w-full")}
    >
      {href ? (
        <Button
          size="lg"
          asChild
          variant={variant === "outline" ? "outline" : "default"}
          className={cn(
            "group relative overflow-hidden font-medium text-lg px-8 py-6 transition-all duration-300 hover:scale-105",
            variant === "primary"
              ? "bg-accent hover:bg-accent/90 text-accent-foreground hover:shadow-xl hover:shadow-primary/30"
              : "border-2 hover:border-primary hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/20",
            className
          )}
        >
          <a href={href} target="_blank" rel="noopener noreferrer">
            {/* Shine sweep on hover */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-primary-foreground/25 to-transparent skew-x-12"
            />
            <span className="relative z-10 inline-flex items-center">{children}</span>
          </a>
        </Button>
      ) : (
        <Button
          size="lg"
          onClick={onClick}
          type={type ?? "button"}
          disabled={disabled}
          variant={variant === "outline" ? "outline" : "default"}
          className={cn(
            "group relative overflow-hidden font-medium text-lg px-8 py-6 transition-all duration-300 hover:scale-105",
            variant === "primary"
              ? "bg-accent hover:bg-accent/90 text-accent-foreground hover:shadow-xl hover:shadow-primary/30"
              : "border-2 hover:border-primary hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/20",
            className
          )}
        >
          {/* Shine sweep on hover */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-primary-foreground/25 to-transparent skew-x-12"
          />
          <span className="relative z-10 inline-flex items-center">{children}</span>
        </Button>
      )}
    </motion.div>
  );
};

export default MagneticButton;
